import { NavLink, Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { AnimatePresence, motion, Reorder } from "framer-motion";
import {
  LayoutDashboard, Package, Network, Boxes, Users as UsersIcon,
  BarChart3, Tags, MonitorPlay, X, ChevronLeft, ChevronRight, ChevronDown, Search, Heart, Star, GripVertical, Image as ImageIcon, Shapes, Landmark, LayoutGrid, Palette, Banknote, Activity, Inbox, BookOpen, BellRing, Presentation, Signal,
} from "lucide-react";
import logoPurple from "@/assets/logo-purple.png";
import brandIcon from "@/assets/icon-purple.png";
import brandIconWhite from "@/assets/icon-white.png";
import { useBranding, resolveAsset } from "@/lib/branding";
import BugReporter from "@/admin/BugReporter";
import { AdminTopBar } from "@/admin/AdminTopBar";
import { SupportModal } from "@/admin/SupportModal";

const DEEP = "var(--brand-primary, #3730A3)";
const ACCENT = "var(--brand-accent, #C026D3)";
const TINT = "#EEF0FB";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard", group: "Overview", roles: ["super_admin", "finance", "kiosk_manager"] },
  { to: "/admin/kiosk-terminal", label: "Kiosk Terminal", icon: MonitorPlay, testid: "nav-kiosk-terminal", group: "Overview", roles: ["super_admin", "kiosk_manager"] },
  { to: "/admin/products", label: "Products", icon: Package, testid: "nav-products", group: "Catalogue", roles: ["super_admin", "kiosk_manager"] },
  { to: "/admin/categories", label: "Categories", icon: Shapes, testid: "nav-categories", group: "Catalogue", roles: ["super_admin"] },
  { to: "/admin/media", label: "Media", icon: ImageIcon, testid: "nav-media", group: "Catalogue", roles: ["super_admin", "kiosk_manager"] },
  { to: "/admin/deals", label: "Meal Deals", icon: Tags, testid: "nav-deals", group: "Catalogue", roles: ["super_admin", "kiosk_manager"] },
  { to: "/admin/hierarchy", label: "Sites & Kiosks", icon: Network, testid: "nav-hierarchy", group: "Kiosk Management", roles: ["super_admin", "kiosk_manager"] },
  { to: "/admin/building-groups", label: "Building Groups", icon: Landmark, testid: "nav-building-groups", group: "Kiosk Management", roles: ["super_admin"] },
  { to: "/admin/planogram", label: "Planograms", icon: LayoutGrid, testid: "nav-planogram", group: "Kiosk Management", roles: ["super_admin", "kiosk_manager"] },
  { to: "/admin/demo-kiosks", label: "Demo Kiosks", icon: Presentation, testid: "nav-demo-kiosks", group: "Kiosk Management", roles: ["super_admin"] },
  { to: "/admin/stock", label: "Stock", icon: Boxes, testid: "nav-stock", group: "Kiosk Management", roles: ["super_admin", "kiosk_manager"] },
  { to: "/admin/offline-alerts", label: "Alert Management", icon: BellRing, testid: "nav-offline-alerts", group: "Kiosk Management", roles: ["super_admin"] },
  { to: "/admin/uptime", label: "Uptime Report", icon: Signal, testid: "nav-uptime", group: "Kiosk Management", roles: ["super_admin", "kiosk_manager"] },
  { to: "/admin/financials", label: "Financials", icon: BarChart3, testid: "nav-financials", group: "Finance", roles: ["super_admin", "finance"] },
  { to: "/admin/reconciliation", label: "Reconciliation", icon: Banknote, testid: "nav-reconciliation", group: "Finance", roles: ["super_admin", "finance"] },
  { to: "/admin/users", label: "Users", icon: UsersIcon, testid: "nav-users", group: "Admin", roles: ["super_admin"] },
  { to: "/admin/login-activity", label: "Login Activity", icon: Activity, testid: "nav-login-activity", group: "Admin", roles: ["super_admin"] },
  { to: "/admin/support-tickets", label: "Support Inbox", icon: Inbox, testid: "nav-support-tickets", group: "Admin", roles: ["super_admin"] },
  { to: "/admin/site-style", label: "Site Style", icon: Palette, testid: "nav-site-style", group: "Admin", roles: ["super_admin"] },
  { to: "/admin/user-guide", label: "User Guide", icon: BookOpen, testid: "nav-user-guide", group: "Help", roles: ["super_admin", "finance", "kiosk_manager"] },
];
const GROUP_ORDER = ["Overview", "Catalogue", "Kiosk Management", "Finance", "Admin", "Help"];

function Sidebar({ items, mobileOpen, onCloseMobile, collapsed, onToggleCollapse, favs, onToggleFav, onReorderFav }) {
  const { pathname } = useLocation();
  const { branding } = useBranding();
  const brandLogo = branding?.logo_light_url ? resolveAsset(branding.logo_light_url) : logoPurple;
  const [q, setQ] = useState("");
  const [openGroups, setOpenGroups] = useState(() => {
    try { return JSON.parse(localStorage.getItem("hv_sidebar_open_groups") || "[]"); } catch { return []; }
  });
  useEffect(() => { localStorage.setItem("hv_sidebar_open_groups", JSON.stringify(openGroups)); }, [openGroups]);
  useEffect(() => {
    const active = items.find((n) => (n.end ? pathname === n.to : pathname.startsWith(n.to)));
    if (active) setOpenGroups((o) => (o.includes(active.group) ? o : [...o, active.group]));
  }, [pathname, items]);
  const toggleGroup = (g) => setOpenGroups((o) => o.includes(g) ? o.filter((x) => x !== g) : [...o, g]);

  const ql = q.toLowerCase();
  const groups = GROUP_ORDER
    .map((g) => ({ name: g, items: items.filter((n) => n.group === g && (!ql || n.label.toLowerCase().includes(ql))) }))
    .filter((g) => g.items.length);

  const favItems = (favs || []).map((tid) => items.find((n) => n.testid === tid)).filter(Boolean);

  const inner = (
    <div className="flex flex-col h-full w-full">
      <div className={`${collapsed ? "px-3" : "px-6"} h-16 border-b border-neutral-200 flex items-center justify-between`}>
        {collapsed ? (
          <Link to="/admin" aria-label="Back to overview" className="block h-10 w-10" data-testid="brand-logo">
            <img src={brandIcon} alt="The Good Box" className="h-10 w-10 rounded-lg object-contain dark:hidden" />
            <img src={brandIconWhite} alt="The Good Box" className="h-10 w-10 rounded-lg object-contain hidden dark:block" />
          </Link>
        ) : (
          <Link to="/admin" className="block" data-testid="brand-logo"><img src={brandLogo} alt="The Good Box" className="w-auto object-contain" style={{ height: "calc(24px * var(--brand-logo-scale, 1))", maxHeight: "44px" }} /></Link>
        )}
        <button className="md:hidden text-neutral-400 hover:text-neutral-800 p-1" onClick={onCloseMobile} aria-label="Close menu" data-testid="mobile-nav-close"><X className="h-5 w-5" /></button>
      </div>

      {!collapsed && (
        <div className="px-3 pt-3">
          <div className="flex items-center border border-neutral-200 bg-neutral-50 rounded">
            <Search className="h-3.5 w-3.5 text-neutral-400 ml-2" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter menu…" data-testid="nav-filter-input" className="flex-1 px-2 py-1.5 text-xs bg-transparent outline-none" />
            {q && <button onClick={() => setQ("")} className="text-neutral-400 hover:text-neutral-800 px-1.5 text-xs">×</button>}
          </div>
        </div>
      )}

      <nav className="flex-1 p-3 space-y-2 overflow-y-auto">
        {!collapsed && !ql && favItems.length > 0 && (
          <div className="pb-2 mb-1 border-b border-neutral-100" data-testid="nav-favourites">
            <p className="px-2 py-1.5 text-[10px] uppercase tracking-[0.16em] font-bold text-neutral-500 flex items-center gap-1.5">
              <Star className="h-3 w-3" style={{ color: ACCENT, fill: ACCENT }} /> Favourites
            </p>
            <Reorder.Group axis="y" values={favItems} onReorder={(arr) => onReorderFav(arr.map((n) => n.testid))} className="space-y-0.5">
              {favItems.map((n) => (
                <Reorder.Item key={n.testid} value={n} className="list-none" data-testid={`fav-item-${n.testid}`}>
                  <div className="group/fav flex items-center gap-1.5 pl-1 pr-1.5 rounded hover:bg-neutral-100">
                    <GripVertical className="h-3.5 w-3.5 text-neutral-300 shrink-0 cursor-grab active:cursor-grabbing" data-testid={`fav-drag-${n.testid}`} />
                    <NavLink
                      to={n.to} end={n.end} data-testid={`fav-link-${n.testid}`}
                      className="flex items-center gap-2 flex-1 min-w-0 py-1.5 text-sm text-neutral-700"
                      style={({ isActive }) => (isActive ? { color: DEEP, fontWeight: 600 } : undefined)}
                    >
                      <n.icon className="h-4 w-4 shrink-0" />
                      <span className="truncate">{n.label}</span>
                    </NavLink>
                    <button onClick={() => onToggleFav(n.testid)} data-testid={`fav-remove-${n.testid}`} title="Remove from favourites" className="opacity-0 group-hover/fav:opacity-100 focus:opacity-100 text-neutral-400 hover:text-rose-500 p-0.5">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </Reorder.Item>
              ))}
            </Reorder.Group>
          </div>
        )}
        {groups.map((g) => {
          const open = collapsed || !!ql || openGroups.includes(g.name);
          return (
            <div key={g.name}>
              {!collapsed && (
                <button onClick={() => toggleGroup(g.name)} data-testid={`nav-group-${g.name.toLowerCase()}`} className="w-full flex items-center justify-between px-2 py-1.5 text-[10px] uppercase tracking-[0.16em] font-bold text-neutral-500 hover:text-neutral-800">
                  <span>{g.name}</span>
                  <ChevronDown className={`h-3 w-3 transition-transform ${open ? "" : "-rotate-90"}`} />
                </button>
              )}
              {open && (
                <div className="space-y-0.5">
                  {g.items.map((n) => {
                    const isFav = (favs || []).includes(n.testid);
                    return (
                      <div key={n.to} className="group/navitem relative flex items-center">
                        <NavLink
                          to={n.to} end={n.end} data-testid={n.testid} title={collapsed ? n.label : undefined}
                          className={({ isActive }) => `flex items-center gap-3 flex-1 ${collapsed ? "justify-center px-2 py-2.5" : "px-3 py-2 pr-8"} text-sm rounded transition-colors ${isActive ? "font-semibold" : "text-neutral-700 hover:bg-neutral-100"}`}
                          style={({ isActive }) => (isActive ? { background: TINT, color: DEEP } : undefined)}
                        >
                          <n.icon className="h-4 w-4 shrink-0" />
                          {!collapsed && <span className="flex-1 truncate">{n.label}</span>}
                        </NavLink>
                        {!collapsed && (
                          <button
                            onClick={() => onToggleFav(n.testid)} data-testid={`fav-toggle-${n.testid}`}
                            title={isFav ? "Remove from favourites" : "Pin to favourites"}
                            className={`absolute right-1.5 p-1 rounded hover:bg-white ${isFav ? "opacity-100" : "opacity-0 group-hover/navitem:opacity-100 focus:opacity-100"}`}
                          >
                            <Star className="h-3.5 w-3.5" style={isFav ? { color: ACCENT, fill: ACCENT } : { color: "#94a3b8" }} />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>
    </div>
  );

  return (
    <>
      <aside
        data-testid="app-sidebar" data-collapsed={collapsed ? "1" : "0"}
        className={`relative z-30 hidden md:flex md:sticky md:top-0 md:h-screen border-r border-neutral-200 bg-white shrink-0 transition-[width] duration-200 ${collapsed ? "md:w-16" : "md:w-64"}`}
      >
        {inner}
        <button
          className="hidden md:inline-flex absolute -right-3 top-8 -translate-y-1/2 z-50 h-6 w-6 items-center justify-center rounded-full border border-neutral-200 bg-white text-neutral-500 shadow-sm transition-colors"
          style={{ }} onMouseEnter={(e) => { e.currentTarget.style.color = DEEP; e.currentTarget.style.borderColor = ACCENT; }} onMouseLeave={(e) => { e.currentTarget.style.color = ""; e.currentTarget.style.borderColor = ""; }}
          onClick={onToggleCollapse} data-testid="sidebar-toggle-collapse" aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? <ChevronRight className="h-3.5 w-3.5" /> : <ChevronLeft className="h-3.5 w-3.5" />}
        </button>
      </aside>

      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }} onClick={onCloseMobile} data-testid="mobile-nav-backdrop" className="fixed inset-0 bg-black/50 z-40 md:hidden" />
            <motion.aside
              initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }} transition={{ type: "tween", duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
              data-testid="app-sidebar-mobile" onClick={(e) => { if (e.target.closest("a")) onCloseMobile(); }}
              className="fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-2xl md:hidden"
            >
              {inner}
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

export default function AdminLayout() {
  const { user } = useAuth();
  const { branding } = useBranding();
  const navigate = useNavigate();
  const location = useLocation();
  const items = NAV.filter((n) => n.roles.includes(user?.role) || (user?.role === "super_duper_admin" && n.roles.includes("super_admin")));

  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("hv_sidebar_collapsed") === "1");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [favs, setFavs] = useState([]);
  const [supportOpen, setSupportOpen] = useState(false);

  useEffect(() => { localStorage.setItem("hv_sidebar_collapsed", collapsed ? "1" : "0"); }, [collapsed]);

  useEffect(() => { api.get("/me/nav-favourites").then((r) => setFavs(r.data.testids || [])).catch(() => {}); }, []);

  const saveFavs = (next) => { setFavs(next); api.put("/me/nav-favourites", { testids: next }).catch(() => {}); };
  const toggleFav = (tid) => saveFavs(favs.includes(tid) ? favs.filter((t) => t !== tid) : [...favs, tid].slice(0, 5));

  useEffect(() => {
    setMobileOpen(false);
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "#F8F9FA" }}>
      {user?.impersonation && (
        <div className="text-white text-xs px-4 py-1.5 flex items-center gap-2" style={{ background: ACCENT }} data-testid="impersonation-banner">
          <span>👤 Viewing as {user.impersonation.name}.</span>
          <button onClick={() => window.location.reload()} className="underline font-semibold">Exit</button>
        </div>
      )}
      <div className="flex flex-1 min-h-0">
        <Sidebar items={items} mobileOpen={mobileOpen} onCloseMobile={() => setMobileOpen(false)} collapsed={collapsed} onToggleCollapse={() => setCollapsed((c) => !c)} favs={favs} onToggleFav={toggleFav} onReorderFav={saveFavs} />
        <main className="flex-1 min-w-0 flex flex-col">
          <AdminTopBar onHamburger={() => setMobileOpen(true)} />
          <div className="max-w-[1500px] mx-auto w-full flex-1">
            <Outlet />
          </div>
          <footer className="px-6 py-4 text-xs text-neutral-400 border-t border-neutral-200 flex flex-wrap gap-x-4 gap-y-1">
            <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" data-testid="footer-privacy" className="hover:text-neutral-600">Privacy</a>
            <a href="/terms" target="_blank" rel="noopener noreferrer" data-testid="footer-terms" className="hover:text-neutral-600">Terms</a>
            <button type="button" data-testid="footer-support" onClick={() => setSupportOpen(true)} className="hover:text-neutral-600">Contact Support</button>
            <span className="ml-auto">© {new Date().getFullYear()} {branding?.brand_name || "The Good Box"}</span>
          </footer>
        </main>
      </div>
      <BugReporter />
      <SupportModal open={supportOpen} onOpenChange={setSupportOpen} />
    </div>
  );
}
